import { Button, ColumnTable, DisplayInfo, Table } from '@octano/global-ui';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../../locales/translations';
import { TeacherFileItemAction } from '../enums/teacher-file-item-action.enum';
import { TeacherFileItem } from '../interfaces/teacher-file-item.interface';
import BadgeState from './BadgeState';

interface Props<T = TeacherFileItem> {
  data: T[];
  isLoadingResults: boolean;
  itemPerPage: number;
  totalItems: number;
  totalPages: number;
  currentPage: number;
  hasActivationPermissions?: boolean;
  onChangePage: (page: number) => void;
  onPressAction: (action: TeacherFileItemAction, item: T) => void;
}

export function TeachersTable({
  data,
  isLoadingResults,
  itemPerPage,
  totalPages,
  totalItems,
  currentPage,
  hasActivationPermissions = false,
  onChangePage,
  onPressAction,
}: Props) {
  const { t } = useTranslation(
    TranslationsKeys.TEACHER_MANAGMENT_TEACHER_FILE_LIST,
  );

  const mapTeacherRole = useCallback(
    (teacher: TeacherFileItem) => {
      if (teacher.isHired && teacher.isTutor) {
        return t(`table.teacherTutor`);
      } else if (teacher.isHired) {
        return t(`table.teacher`);
      } else if (teacher.isTutor) {
        return t(`table.tutor`);
      } else {
        return '';
      }
    },
    [t],
  );

  const columns = useMemo(() => {
    const cols: ColumnTable<TeacherFileItem>[] = [
      {
        headerText: t(`table.role`),
        columnName: 'role',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: 150,
        cellFormat: ({ row }) => (
          <>
            {(row.isHired || row.isTutor) && (
              <div
                style={{
                  padding: '5px 0',
                  borderRadius: 5,
                  border: '1px solid var(--primary)',
                  color: 'var(--primary)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                className="text-capitalize"
              >
                {mapTeacherRole(row)}
              </div>
            )}
          </>
        ),
      },
      {
        headerText: t(`table.name`),
        columnName: 'identification',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: 200,
        cellFormat: ({ row }) => (
          <div>
            <span className="d-block text-center">{row?.name}</span>
            <span className="d-block text-center">{row?.identification}</span>
          </div>
        ),
      },
      {
        headerText: t(`table.email`),
        columnName: 'email',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: 200,
      },
      {
        headerText: t(`table.school`),
        columnName: 'school',
        tdClassName: 'text-center',
        thClassName: 'text-center',
      },
      {
        headerText: t(`table.campus`),
        columnName: 'campus',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: 180,
      },
      {
        headerText: t(`table.status`),
        columnName: 'status',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: 180,
        cellFormat: ({ value }) => (
          <>
            <BadgeState text={t(`statuses.${value}`)} status={value} />
          </>
        ),
      },
      {
        headerText: t(`table.status`),
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        width: 170 * (hasActivationPermissions ? 2 : 1) + 15,
        cellFormat: ({ row }) => (
          <>
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                gap: 5,
              }}
            >
              {row.actions.includes(TeacherFileItemAction.Edit) && (
                <Button
                  type="button"
                  text={t(`actions.edit`)}
                  style={{ width: 170 }}
                  size="sm"
                  onClick={() => onPressAction(TeacherFileItemAction.Edit, row)}
                />
              )}
            </div>
          </>
        ),
      },
    ];
    return cols;
  }, [hasActivationPermissions, t, onPressAction, mapTeacherRole]);

  return (
    <div className="w-100">
      <Table
        isLoadingResults={isLoadingResults}
        data={data}
        columns={columns}
        pagination={{
          currentPage: currentPage + 1,
          itemsPerPage: itemPerPage,
          totalItems: totalItems,
          totalPages: totalPages,
          onChangePage: (page) => onChangePage(page - 1),
        }}
        noResultsText={
          <div className="d-flex justify-content-center align-items-center py-5">
            <DisplayInfo
              iconName="information"
              textBody={t(`noResults.description`)}
              maxWidth="650px"
            />
          </div>
        }
      />
    </div>
  );
}
